import React, { Fragment, useState } from 'react';
import moment from 'moment';
import NumericalLabel from 'react-pretty-numbers';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tabs, Tab, TableRow, TableCell } from '@material-ui/core';
import { TableListComp } from '../../../shared_elements';
import { permissionCheckFn, getCurrencyFormat } from '../../../utils';
import { trackActivity } from '../../../utils/mixpanel';

require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/no-data-to-display')(Highcharts);

const formatCurrency = (value, digits = 3) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

const BlrProjection = ({ logResult }) => {
  const [modal, setModal] = useState(false);
  const [tab, setTab] = useState('values');
  const inflation = logResult.escalation_percentage != null ? logResult.escalation_percentage : 0;
  const futureKeys = logResult.future_blr_value ? Object.keys(logResult.future_blr_value) : [];

  const getPlots = (projection) => {
    if (!projection) return [];
    return Object.keys(projection).map((year) => [
      new Date(`${year.slice(4, 6) || '01'}/01/${year.slice(0, 4)}`).getTime(),
      projection[year],
    ]);
  };

  const buildSeries = () => {
    const series = [];
    if (logResult.future_blr_value) {
      series.push({ name: 'BLR @ 0%', data: getPlots(logResult.future_blr_value), color: '#7fb2ff' });
    }
    if (inflation && logResult.inflated_future_blr_value) {
      series.push({ name: `BLR @ ${inflation}%`, data: getPlots(logResult.inflated_future_blr_value), color: '#2b2b3a' });
    }
    return series;
  };

  const chartOptions = {
    chart: { type: 'line', marginTop: 40 },
    title: { text: null },
    xAxis: { type: 'datetime', title: { text: 'Projection Year(s)' } },
    yAxis: {
      title: { text: 'US$ k' },
      labels: {
        formatter: function () {
          return formatCurrency(this.value, 3);
        },
      },
    },
    tooltip: {
      formatter: function () {
        return `${this.series.name}<br/>The value as of <b>${moment(this.x).format('MMM YYYY')}</b> is <b>${formatCurrency(this.y, 3)} k</b>`;
      },
    },
    series: buildSeries(),
    exporting: permissionCheckFn({ secondaryKey: 'future_value', keyIndex: 'U' }) ? {
      buttons: {
        contextButton: {
          menuItems: [{
            text: 'Export as PNG',
            onclick: function () {
              this.exportChart();
              trackActivity('Report Exported', { file_type: 'PNG', report_type: 'Chart', type: 'Future Value' });
            },
          }],
        },
      },
    } : false,
    credits: { enabled: false },
  };

  const currencyCell = (value) => (
    value != null ? (
      <Fragment>
        <NumericalLabel params={getCurrencyFormat({ shortFormat: false, precision: 3 })}>{value}</NumericalLabel> k
      </Fragment>
    ) : '—'
  );

  return (
    <Fragment>
      <Button onClick={() => setModal(true)} color="primary" variant="contained" size="small">Show</Button>
      <Dialog
        open={modal}
        onClose={() => setModal(false)}
        maxWidth="lg"
        fullWidth
        className="engine-future-projections-modal"
      >
        <DialogTitle className="engine-modal-head">Future Projections</DialogTitle>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary" className="engine-modal-tabs">
          <Tab label="Values" value="values" />
          <Tab label="Graph" value="graph" />
        </Tabs>
        <DialogContent dividers className="engine-modal-body">
          {tab === 'values' ? (
            <Fragment>
              <h4 className="engine-modal-subhead">Future Base Lease Rate (BLR) <span>(US$ k)</span></h4>
              <TableListComp
                heads={[
                  { label: 'Date', sortOption: false },
                  { label: 'BLR @ 0% Inflation', sortOption: false },
                  ...(inflation ? [{ label: `BLR @ ${inflation}% Inflation`, sortOption: false }] : []),
                ]}
                data={futureKeys.map((label) => {
                  const date = new Date(label.slice(0, 4), `${label.slice(4, 6) || '01'}` - 1);
                  return (
                    <TableRow key={label}>
                      <TableCell>{moment(date).format('MMM YYYY')}</TableCell>
                      <TableCell>{currencyCell(logResult.future_blr_value[label])}</TableCell>
                      {inflation ? (
                        <TableCell>
                          {currencyCell(logResult.inflated_future_blr_value && logResult.inflated_future_blr_value[label])}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              />
            </Fragment>
          ) : (
            <Fragment>
              <h4 className="engine-modal-subhead">Future Base Lease Rate (BLR)</h4>
              <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            </Fragment>
          )}
        </DialogContent>
        <DialogActions className="engine-modal-foot">
          <Button onClick={() => setModal(false)} color="primary" variant="outlined" size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default BlrProjection;
